/**
 * Arena lifecycle service (ADR-031 P4).
 * Organizer CRUD, publish, archive, delete draft for `arenas/{arn_*}`.
 * Live Session linking: `liveSessionLinkService` / `liveSessionService`.
 * Casual room-hosted draw view remains in `competitionService.js` until Live Session rename wave.
 */

import * as arenaRepository from "../repositories/arenaRepository";
import {
  COMPETITION_KINDS,
  COMPETITION_STATUSES,
  createCompetitionShell,
  newCompetitionId,
} from "../domain/competition/competitionTypes";
import { buildQuickCreatePayload, validateQuickCreateInput } from "../domain/competition/quickCreateDefaults";
import {
  validateStatusTransitionGuards,
  normalizeCompetitionStatus,
  isCompetitionSoftDeleted,
} from "../domain/competition/competitionStatusTransitions";
import {
  alignStartOnOrAfterRegistrationClose,
  formToCompetitionShape,
  validateCompetition,
} from "../domain/competition/competitionValidation";
import { DEFAULT_SPORT } from "../domain/sport/sportTypes";
import {
  canArchiveCompetition,
  canDeleteDraftCompetition,
  canEditCompetition,
  canHardDeleteCompetition,
  canPublishCompetition,
  canSoftDeleteCompetition,
  isCompetitionOwner,
} from "../permissions/competitionPermissions";
import { deleteField } from "firebase/firestore";
import { auth } from "../firebase/config";
import {
  trackCompetitionCreated,
  trackCompetitionPublished,
} from "./analytics/bridges/competitionAnalytics";

function currentUid() {
  return auth.currentUser?.uid || null;
}

function friendlyFirestoreError(err) {
  const code = err?.code || "";
  if (code === "permission-denied") {
    return new Error("You do not have permission to change this competition.");
  }
  if (code === "unavailable" || code === "failed-precondition") {
    return new Error("You appear to be offline. Try again when connected.");
  }
  if (err?.message) return err instanceof Error ? err : new Error(String(err.message));
  return new Error("Something went wrong saving the competition.");
}

/**
 * Strip undefined (and optional nulls that break Firestore rules) recursively.
 * @param {object} obj
 * @param {{ dropNullKeys?: string[] }} [opts]
 */
function sanitizeForFirestore(obj, opts = {}) {
  const dropNullKeys = new Set(opts.dropNullKeys || ["draw"]);
  if (obj == null || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map((item) => sanitizeForFirestore(item, opts));
  /** @type {Record<string, unknown>} */
  const out = {};
  Object.keys(obj).forEach((key) => {
    const v = obj[key];
    if (v === undefined) return;
    if (v === null && dropNullKeys.has(key)) return;
    out[key] = typeof v === "object" && v !== null ? sanitizeForFirestore(v, opts) : v;
  });
  return out;
}

/** Arena must not embed entries/draws; Event summaries may mirror until subcollection seed is confirmed. */
function stripArenaCompetitiveFields(doc) {
  if (!doc || typeof doc !== "object") return doc;
  const {
    managedEntries: _en,
    managedDraws: _d,
    __competitiveSource: _src,
    id: _id,
    ...rest
  } = doc;
  return {
    ...rest,
    managedEntries: [],
    managedDraws: [],
  };
}

function eventSummariesForArena(managedEvents) {
  return (Array.isArray(managedEvents) ? managedEvents : [])
    .map((e) => ({
      eventId: e.eventId || null,
      name: String(e.display?.name ?? e.name ?? "").trim() || "Event",
      display: e.display || { name: String(e.name || "").trim() || "Event" },
      competition: e.competition || null,
      status: e.status || "draft",
      createdAtMs: e.createdAtMs || Date.now(),
    }))
    .filter((e) => e.name);
}

/**
 * Seed Event subcollections from in-memory managedEvents (create/wizard).
 * @param {string} arenaId
 * @param {object[]} managedEvents
 * @param {string} createdBy
 */
async function seedEventsIfNeeded(arenaId, managedEvents, createdBy) {
  const { ensureDefaultEvent, listEvents, seedEventsFromNames } = await import(
    "./tournamentEventService"
  );
  const { upsertEvent } = await import("../repositories/tournamentEventRepository");
  const { syncEventRegistry } = await import("./tournamentEventState");
  const { createTournamentEventShell } = await import("../domain/event/eventTypes");

  const existing = await listEvents(arenaId);
  if (existing.length) return existing;

  const list = Array.isArray(managedEvents) ? managedEvents : [];
  if (!list.length) {
    return [await ensureDefaultEvent(arenaId, { name: "Main event" })];
  }

  try {
    const created = [];
    for (const ev of list) {
      const shell = createTournamentEventShell({
        ...ev,
        arenaId,
        createdBy: createdBy || ev.createdBy || null,
      });
      await upsertEvent(arenaId, shell);
      created.push(shell);
    }
    await syncEventRegistry(
      arenaId,
      created.map((e) => e.eventId)
    );
    return created;
  } catch (firstErr) {
    // Fallback: seed by name only (Host Tournament chips path).
    const names = list.map((e) => e.name || e).map((n) => String(n || "").trim()).filter(Boolean);
    if (!names.length) throw firstErr;
    return seedEventsFromNames(arenaId, names, createdBy);
  }
}

function plannedNamesFromEvents(managedEvents) {
  return (Array.isArray(managedEvents) ? managedEvents : [])
    .map((e) => String(e?.name || "").trim())
    .filter(Boolean);
}

/**
 * @param {Partial<import('../domain/competition/competitionTypes').Competition>} patch
 * @param {string} uid
 */
function buildOwnerIds(patch, uid) {
  const createdBy = patch.createdBy || uid;
  const managers = Array.isArray(patch.managerUids) ? patch.managerUids : [];
  return [...new Set([createdBy, ...managers].filter(Boolean))];
}

/**
 * Create with essentials: name + start/end + ≥1 event (+ optional venue/org/cover).
 * Also provisions a linked Arena room + default courts (best-effort).
 * @param {{
 *   name: string,
 *   startMs: number,
 *   endMs: number,
 *   eventNames: string[],
 *   venueName?: string,
 *   registrationOpenMs?: number | null,
 *   registrationCloseMs?: number | null,
 *   organizationId?: string | null,
 *   bannerImageUrl?: string | null,
 * }} input
 */
export async function createQuickCompetition(input = {}) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in to create a competition.");

  const validation = validateQuickCreateInput(input);
  if (!validation.ok) {
    const err = new Error(validation.errors[0] || "Tournament is incomplete.");
    err.validation = validation;
    throw err;
  }

  let organizerName = String(input.organizerName || "").trim();
  if (!organizerName || organizerName === "Organizer") {
    try {
      const { ensureMyPlayer } = await import("./playerProfileService");
      const player = await ensureMyPlayer(uid, auth.currentUser?.displayName || "");
      organizerName = String(player?.displayName || "").trim();
    } catch {
      organizerName = "";
    }
  }
  if (!organizerName) organizerName = auth.currentUser?.displayName || "Organizer";

  const payload = buildQuickCreatePayload({
    ...input,
    organizerUid: uid,
    organizerName,
  });

  const created = await createCompetition(payload);
  let roomId = null;
  let roomDisplayName = created.name || null;
  const arenaId = created.arenaId || created.competitionId;

  try {
    const { ensureTournamentModule } = await import("./tournamentModuleService");
    await ensureTournamentModule(arenaId, { name: created.name });
  } catch {
    /* module bootstrap best-effort */
  }

  // Re-seed / sync Event registry after module exists (Host Tournament chips → Event docs).
  try {
    const { listEvents, seedEventsFromDefinitions, seedEventsFromNames } = await import(
      "./tournamentEventService"
    );
    const { syncEventRegistry } = await import("./tournamentEventState");
    let events = await listEvents(arenaId);
    if (!events.length) {
      const managed = Array.isArray(payload.managedEvents) ? payload.managedEvents : [];
      if (managed.length) {
        events = await seedEventsFromDefinitions(arenaId, managed, uid);
      } else {
        const names =
          (Array.isArray(payload.plannedEventNames) && payload.plannedEventNames.length
            ? payload.plannedEventNames
            : plannedNamesFromEvents(payload.managedEvents)) || [];
        if (names.length) {
          events = await seedEventsFromNames(arenaId, names, uid);
        }
      }
    }
    if (events.length) {
      await syncEventRegistry(
        arenaId,
        events.map((e) => e.eventId)
      );
      created.managedEvents = events.map((e) => ({
        eventId: e.eventId,
        name: e.display?.name || e.name,
        display: e.display,
        competition: e.competition,
        status: e.status,
        createdAtMs: e.createdAtMs,
      }));
    }
  } catch {
    /* hydrate/publish recover via plannedEventNames */
  }

  try {
    const { ensureTournamentRoomForCompetition } = await import(
      "./liveSessionLinkService"
    );
    const ensured = await ensureTournamentRoomForCompetition(arenaId);
    if (ensured?.ok && ensured.roomId) {
      roomId = ensured.roomId;
      roomDisplayName = ensured.roomDisplayName || roomDisplayName;
    }
  } catch {
    /* room provision is best-effort — Play / draws will retry */
  }

  try {
    const { ensureDefaultCourts } = await import("./arenaCourtService");
    await ensureDefaultCourts(arenaId);
  } catch {
    /* courts optional until Play */
  }

  return {
    ...created,
    arenaId,
    roomId,
    liveSessionId: roomId,
    roomDisplayName,
  };
}

/**
 * Close registration early (or mark closed after window). Unlocks format/generate.
 * @param {string} competitionId
 */
export async function closeCompetitionRegistration(competitionId) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in required.");
  const existing = await arenaRepository.fetchCompetition(competitionId);
  if (!existing) throw new Error("Competition not found.");
  if (!canEditCompetition(existing, uid) && !isCompetitionOwner(existing, uid)) {
    throw new Error("You do not have permission to close registration.");
  }

  const status = normalizeCompetitionStatus(existing.status);
  if (status === COMPETITION_STATUSES.DRAFT) {
    throw new Error("Publish the tournament before closing registration.");
  }
  if (
    status === COMPETITION_STATUSES.REGISTRATION_CLOSED ||
    status === COMPETITION_STATUSES.IN_PROGRESS ||
    status === COMPETITION_STATUSES.COMPLETED
  ) {
    return existing;
  }

  const now = Date.now();
  const dates = alignStartOnOrAfterRegistrationClose({
    ...(existing.dates || {}),
    registrationCloseMs: existing.dates?.registrationCloseMs ?? now,
  });

  if (
    status === COMPETITION_STATUSES.PUBLISHED ||
    status === COMPETITION_STATUSES.REGISTRATION_OPEN ||
    status === COMPETITION_STATUSES.DRAW_PUBLISHED
  ) {
    await arenaRepository.updateCompetition(competitionId, {
      dates,
      status: COMPETITION_STATUSES.REGISTRATION_CLOSED,
      updatedAtMs: now,
    });
    return {
      ...existing,
      dates,
      status: COMPETITION_STATUSES.REGISTRATION_CLOSED,
      updatedAtMs: now,
    };
  }

  throw new Error("Registration cannot be closed from the current status.");
}

/**
 * Open registration again after it was closed, before the tournament starts.
 * Clears the close timestamp that Close registration writes, so the public
 * page does not keep saying registration has already ended.
 * @param {string} competitionId
 */
export async function reopenCompetitionRegistration(competitionId) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in required.");
  const existing = await arenaRepository.fetchCompetition(competitionId, { fromServer: true });
  if (!existing) throw new Error("Competition not found.");
  if (!canEditCompetition(existing, uid) && !isCompetitionOwner(existing, uid)) {
    throw new Error("You do not have permission to reopen registration.");
  }

  const status = normalizeCompetitionStatus(existing.status);
  if (
    status === COMPETITION_STATUSES.REGISTRATION_OPEN ||
    status === COMPETITION_STATUSES.PUBLISHED
  ) {
    return existing;
  }
  if (status !== COMPETITION_STATUSES.REGISTRATION_CLOSED) {
    throw new Error("Registration can only be reopened after it has been closed.");
  }

  const now = Date.now();
  const dates = { ...(existing.dates || {}) };
  delete dates.registrationCloseMs;
  await arenaRepository.updateArena(competitionId, {
    status: COMPETITION_STATUSES.REGISTRATION_OPEN,
    "dates.registrationCloseMs": deleteField(),
    updatedAtMs: now,
  });
  return {
    ...existing,
    dates,
    status: COMPETITION_STATUSES.REGISTRATION_OPEN,
    updatedAtMs: now,
  };
}

/**
 * Create a draft competition (platform management, not room-owned).
 * @param {object} input
 */
export async function createCompetition(input = {}) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in to create an arena.");

  const competitionId = input.competitionId || input.arenaId || newCompetitionId();
  const shell = createCompetitionShell({
    ...input,
    competitionId,
    arenaId: input.arenaId || competitionId,
    createdBy: uid,
    organizerUid: input.organizerUid || uid,
    organizerName: input.organizerName || auth.currentUser?.displayName || "Organizer",
    status: COMPETITION_STATUSES.DRAFT,
    sport: input.sport || input.sportId || DEFAULT_SPORT,
    sportId: input.sportId || input.sport || DEFAULT_SPORT,
    mode: input.mode || "tournament",
    enabledModules: input.enabledModules || ["tournament"],
    kind: input.kind || COMPETITION_KINDS.TOURNAMENT_DRAW,
    roomId: input.liveSessionId ?? input.roomId ?? null,
    liveSessionId: input.liveSessionId ?? input.roomId ?? null,
    ownerIds: buildOwnerIds({ ...input, createdBy: uid }, uid),
  });

  const plannedEventNames =
    (Array.isArray(input.plannedEventNames) && input.plannedEventNames.length
      ? input.plannedEventNames
      : plannedNamesFromEvents(input.managedEvents || shell.managedEvents)
    ).map((n) => String(n || "").trim()).filter(Boolean);

  const validation = validateCompetition(shell, { forDraftSave: true });
  if (!validation.ok) {
    const err = new Error(validation.errors[0] || "Arena is invalid.");
    err.validation = validation;
    throw err;
  }

  try {
    await arenaRepository.createCompetition(
      competitionId,
      sanitizeForFirestore(
        stripArenaCompetitiveFields({
          ...shell,
          plannedEventNames,
          // Durable fallback if Event subcollection seed fails (rules/deploy).
          managedEvents: eventSummariesForArena(
            input.managedEvents || shell.managedEvents
          ),
        })
      )
    );
  } catch (err) {
    throw friendlyFirestoreError(err);
  }

  let seededEvents = [];
  let seedError = null;
  try {
    seededEvents = await seedEventsIfNeeded(
      competitionId,
      input.managedEvents || shell.managedEvents,
      uid
    );
  } catch (err) {
    seedError = err;
    try {
      const { seedEventsFromNames } = await import("./tournamentEventService");
      if (plannedEventNames.length) {
        seededEvents = await seedEventsFromNames(competitionId, plannedEventNames, uid);
        seedError = null;
      }
    } catch (err2) {
      seedError = err2;
    }
  }

  if (!seededEvents.length && plannedEventNames.length) {
    try {
      const { seedEventsFromNames } = await import("./tournamentEventService");
      seededEvents = await seedEventsFromNames(competitionId, plannedEventNames, uid);
      seedError = null;
    } catch (err) {
      seedError = err;
    }
  }

  // Always keep Arena.managedEvents in sync so publish/UI work even when subcollection writes fail.
  const mirrorEvents = seededEvents.length
    ? eventSummariesForArena(seededEvents)
    : eventSummariesForArena(input.managedEvents || shell.managedEvents);
  if (mirrorEvents.length) {
    try {
      await arenaRepository.updateCompetition(competitionId, {
        managedEvents: mirrorEvents,
        plannedEventNames,
        eventId: mirrorEvents[0]?.eventId || null,
        updatedAtMs: Date.now(),
      });
    } catch {
      /* mirror best-effort */
    }
  }

  if (!mirrorEvents.length && seedError) {
    throw friendlyFirestoreError(
      seedError || new Error("Could not create tournament events. Try again.")
    );
  }

  try {
    trackCompetitionCreated({
      format: shell.kind || "tournament_draw",
      entrantCount: shell.entries?.length || 0,
    });
  } catch {
    /* analytics never blocks */
  }

  return {
    ...shell,
    arenaId: competitionId,
    plannedEventNames,
    managedEvents: mirrorEvents.length
      ? mirrorEvents
      : seededEvents.length
        ? eventSummariesForArena(seededEvents)
        : shell.managedEvents,
    eventId:
      mirrorEvents[0]?.eventId ||
      seededEvents[0]?.eventId ||
      shell.eventId ||
      null,
    validation,
  };
}

/**
 * @param {string} competitionId
 * @param {object} patch
 */
export async function updateCompetition(competitionId, patch) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in to edit a competition.");

  const existing = await arenaRepository.fetchCompetition(competitionId);
  if (!existing) throw new Error("Competition not found.");
  if (!canEditCompetition(existing, uid)) {
    throw new Error("You do not have permission to edit this competition.");
  }

  const merged = {
    ...existing,
    ...patch,
    competitionId,
    createdBy: existing.createdBy,
    updatedAtMs: Date.now(),
    ownerIds: buildOwnerIds(
      {
        createdBy: existing.createdBy,
        managerUids: patch.managerUids ?? existing.managerUids,
      },
      existing.createdBy
    ),
    entryCount:
      patch.entryCount ??
      (Array.isArray(patch.entries) ? patch.entries.length : existing.entryCount) ??
      (existing.entries || []).length,
  };

  if (patch.settings?.defaultMatchFormat?.setsToWin != null) {
    merged.setsToWin = patch.settings.defaultMatchFormat.setsToWin;
  }

  const validation = validateCompetition(merged, {
    forDraftSave: normalizeCompetitionStatus(merged.status) === COMPETITION_STATUSES.DRAFT,
    forPublish: false,
  });
  if (!validation.ok) {
    const err = new Error(validation.errors[0] || "Competition is invalid.");
    err.validation = validation;
    throw err;
  }

  try {
    if (Array.isArray(patch.managedEvents)) {
      const { upsertEvent, listEvents, deleteEvent } = await import(
        "../repositories/tournamentEventRepository"
      );
      const { syncEventRegistry } = await import("./tournamentEventState");
      const { createTournamentEventShell } = await import("../domain/event/eventTypes");
      /** Use ids after shell creation — never prune with an empty Set while upserting. */
      const nextIds = new Set();
      for (const ev of patch.managedEvents) {
        const shell = createTournamentEventShell({
          ...ev,
          arenaId: competitionId,
          createdBy: uid,
        });
        await upsertEvent(competitionId, shell);
        if (shell.eventId) nextIds.add(shell.eventId);
      }
      const existingEvents = await listEvents(competitionId);
      if (patch.managedEvents.length === 0 || nextIds.size > 0) {
        await Promise.all(
          existingEvents
            .filter((e) => !nextIds.has(e.eventId))
            .map((e) => deleteEvent(competitionId, e.eventId))
        );
      }
      await syncEventRegistry(competitionId, [...nextIds]);
      merged.plannedEventNames = plannedNamesFromEvents(patch.managedEvents);
      merged.managedEvents = eventSummariesForArena(patch.managedEvents);
    }

    await arenaRepository.setCompetition(
      competitionId,
      sanitizeForFirestore(
        stripArenaCompetitiveFields({
          ...merged,
          managedEvents: Array.isArray(merged.managedEvents)
            ? eventSummariesForArena(merged.managedEvents)
            : eventSummariesForArena(existing.managedEvents),
          plannedEventNames:
            merged.plannedEventNames ||
            plannedNamesFromEvents(merged.managedEvents || existing.managedEvents),
          id: undefined,
        })
      )
    );
  } catch (err) {
    throw friendlyFirestoreError(err);
  }

  return { competition: merged, validation };
}

/**
 * Upload compressed competition theme / banner photo (one object, overwritten).
 * Falls back to local compressed data URL when Firebase Storage is unavailable.
 * @param {string} competitionId
 * @param {File} file
 */
export async function uploadCompetitionThemePhoto(competitionId, file) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in to edit a competition.");
  const existing = await arenaRepository.fetchCompetition(competitionId);
  if (!existing) throw new Error("Competition not found.");
  if (!canEditCompetition(existing, uid)) {
    throw new Error("You do not have permission to edit this competition.");
  }
  const { uploadEntityThemePhoto } = await import("../repositories/cloudMediaStorage");
  const { uploadEntityThemePhotoWithLocalFallback } = await import(
    "../repositories/localEntityThemePhotoStorage"
  );
  const { url: bannerImageUrl } = await uploadEntityThemePhotoWithLocalFallback({
    kind: "competition",
    entityId: competitionId,
    file,
    uploadCloud: uploadEntityThemePhoto,
  });
  return updateCompetition(competitionId, {
    bannerImageUrl,
    coverImageUrl: bannerImageUrl,
  });
}

/**
 * @param {string} competitionId
 */
export async function removeCompetitionThemePhoto(competitionId) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in to edit a competition.");
  const existing = await arenaRepository.fetchCompetition(competitionId);
  if (!existing) throw new Error("Competition not found.");
  if (!canEditCompetition(existing, uid)) {
    throw new Error("You do not have permission to edit this competition.");
  }
  const { deleteEntityThemePhoto } = await import("../repositories/cloudMediaStorage");
  const { removeLocalEntityThemePhoto } = await import(
    "../repositories/localEntityThemePhotoStorage"
  );
  removeLocalEntityThemePhoto("competition", competitionId);
  try {
    await deleteEntityThemePhoto("competition", competitionId);
  } catch {
    /* ignore */
  }
  return updateCompetition(competitionId, {
    bannerImageUrl: null,
    coverImageUrl: null,
  });
}

/**
 * @param {object} competition
 */
export function validateCompetitionFields(competition, options) {
  return validateCompetition(competition, options);
}

/**
 * @param {string} competitionId
 * @param {string} toStatus
 */
export async function transitionCompetitionStatus(competitionId, toStatus) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in required.");

  try {
    return await arenaRepository.runCompetitionTransaction(competitionId, (current) => {
      if (
        toStatus === COMPETITION_STATUSES.PUBLISHED &&
        !canPublishCompetition(current, uid)
      ) {
        return { error: "Only the competition owner can publish.", next: {} };
      }
      if (
        toStatus === COMPETITION_STATUSES.ARCHIVED &&
        !canArchiveCompetition(current, uid)
      ) {
        return { error: "Only the owner can archive a completed competition.", next: {} };
      }
      if (
        toStatus === COMPETITION_STATUSES.DELETED &&
        !canSoftDeleteCompetition(current, uid)
      ) {
        return { error: "Only the owner can soft-delete this competition.", next: {} };
      }
      if (
        toStatus !== COMPETITION_STATUSES.PUBLISHED &&
        toStatus !== COMPETITION_STATUSES.ARCHIVED &&
        toStatus !== COMPETITION_STATUSES.DELETED &&
        !canEditCompetition(current, uid) &&
        !isCompetitionOwner(current, uid)
      ) {
        return { error: "You do not have permission to change status.", next: {} };
      }

      const guard = validateStatusTransitionGuards(current, toStatus);
      if (!guard.ok) return { error: guard.error, next: {} };

      if (toStatus === COMPETITION_STATUSES.PUBLISHED) {
        const pubVal = validateCompetition(current, { forPublish: true });
        if (!pubVal.ok) {
          return { error: pubVal.errors[0] || "Competition is not ready to publish.", next: {} };
        }
      }

      return {
        next: {
          status: toStatus,
          updatedAtMs: Date.now(),
          ...(toStatus === COMPETITION_STATUSES.ARCHIVED
            ? { archivedAtMs: Date.now() }
            : {}),
          ...(toStatus === COMPETITION_STATUSES.DELETED
            ? { deletedAtMs: Date.now() }
            : {}),
          ...(toStatus === COMPETITION_STATUSES.PUBLISHED
            ? { publishedAtMs: Date.now() }
            : {}),
        },
      };
    });
  } catch (err) {
    throw friendlyFirestoreError(err);
  }
}

async function transitionCompetitionStatusWithNotify(competitionId, toStatus) {
  const existing = await arenaRepository.fetchCompetition(competitionId);
  const previous = existing?.status;
  const updated = await transitionCompetitionStatus(competitionId, toStatus);
  try {
    const { notifyCompetitionLifecycle } = await import("./tournamentEntryService");
    notifyCompetitionLifecycle(updated || { ...existing, status: toStatus }, previous);
  } catch {
    /* ignore */
  }
  return updated;
}

/**
 * @param {string} competitionId
 * @param {{ fallbackEvents?: object[], fallbackEventNames?: string[] }} [opts]
 */
export async function publishCompetition(competitionId, opts = {}) {
  const existing = await arenaRepository.fetchCompetition(competitionId);
  if (!existing) throw new Error("Competition not found.");

  const { listEvents, ensureDefaultEvent, seedEventsFromNames } = await import(
    "./tournamentEventService"
  );
  const { toManagedEventSummaries } = await import("./tournamentEventState");
  const uid = currentUid();

  let events = [];
  try {
    events = await listEvents(competitionId);
  } catch (err) {
    throw friendlyFirestoreError(err);
  }

  if (!events.length) {
    const fallback = Array.isArray(opts.fallbackEvents) ? opts.fallbackEvents : [];
    const planned = [
      ...(Array.isArray(opts.fallbackEventNames) ? opts.fallbackEventNames : []),
      ...(Array.isArray(existing.plannedEventNames) ? existing.plannedEventNames : []),
      ...plannedNamesFromEvents(fallback),
      ...plannedNamesFromEvents(existing.managedEvents),
    ]
      .map((n) => String(n || "").trim())
      .filter(Boolean);
    const uniqueNames = [...new Set(planned)];

    try {
      if (fallback.length) {
        events = await seedEventsIfNeeded(competitionId, fallback, uid);
      }
      if (!events.length && uniqueNames.length) {
        events = await seedEventsFromNames(competitionId, uniqueNames, uid);
      }
      if (!events.length) {
        await ensureDefaultEvent(competitionId, { name: "Main event" });
        events = await listEvents(competitionId);
      }
    } catch {
      /* Fall through to Arena.managedEvents mirror below */
    }
  }

  // Pre-launch fallback: Event subcollection may be blocked by rules; Arena.managedEvents is durable.
  const mirrorEvents = eventSummariesForArena(
    events.length ? toManagedEventSummaries(events) : existing.managedEvents
  );
  const fromOpts = eventSummariesForArena(opts.fallbackEvents);
  const publishEvents = mirrorEvents.length
    ? mirrorEvents
    : fromOpts.length
      ? fromOpts
      : eventSummariesForArena(
          (existing.plannedEventNames || []).map((name) => ({ name, eventId: null }))
        );

  if (!publishEvents.length) {
    const err = new Error(
      "Publishing requires at least one event. Add an event on the Events tab, then try again."
    );
    err.validation = validateCompetition(
      { ...existing, managedEvents: [] },
      { forPublish: true }
    );
    throw err;
  }

  // Always mirror event evidence onto the Arena doc so the status transaction's
  // forPublish validation (which reads the raw Firestore doc) cannot fail when
  // competitive state lives only in Event subcollections.
  try {
    await arenaRepository.updateCompetition(competitionId, {
      managedEvents: publishEvents,
      plannedEventNames: plannedNamesFromEvents(publishEvents),
      eventId: publishEvents[0]?.eventId || null,
      updatedAtMs: Date.now(),
    });
  } catch {
    /* mirror best-effort — hydrate below still carries publishEvents */
  }

  const hydrated =
    (await arenaRepository.fetchCompetition(competitionId, { fromServer: true })) ||
    existing;
  const withEvent = {
    ...hydrated,
    managedEvents: publishEvents,
    plannedEventNames: plannedNamesFromEvents(publishEvents),
    status: COMPETITION_STATUSES.DRAFT,
  };

  const validation = validateCompetition(withEvent, { forPublish: true });
  if (!validation.ok) {
    const err = new Error(validation.errors[0] || "Cannot publish.");
    err.validation = validation;
    throw err;
  }

  let updated = await transitionCompetitionStatusWithNotify(
    competitionId,
    COMPETITION_STATUSES.PUBLISHED
  );

  // Publish opens registration when self-register is allowed (draft → published → open).
  const allowSelf =
    updated?.settings?.registration?.allowSelfRegister !== false;
  if (allowSelf && updated) {
    try {
      updated = await transitionCompetitionStatusWithNotify(
        competitionId,
        COMPETITION_STATUSES.REGISTRATION_OPEN
      );
    } catch {
      /* keep published if open-registration transition is blocked */
    }
  }

  try {
    trackCompetitionPublished({
      format: updated?.kind || "tournament_draw",
      entrantCount: updated?.entries?.length || updated?.entryCount || 0,
    });
  } catch {
    /* ignore */
  }

  const fresh = await arenaRepository.fetchCompetition(competitionId, {
    fromServer: true,
  });
  return fresh || updated;
}

/**
 * @param {string} competitionId
 */
export async function archiveCompetition(competitionId) {
  return transitionCompetitionStatus(competitionId, COMPETITION_STATUSES.ARCHIVED);
}

/**
 * Soft-delete competition (default). Document remains for history/stats.
 * @param {string} competitionId
 */
export async function softDeleteCompetition(competitionId) {
  return transitionCompetitionStatus(competitionId, COMPETITION_STATUSES.DELETED);
}

/**
 * Permanent delete of competition document. Does not remove matchRecords.
 * @param {string} competitionId
 */
export async function hardDeleteCompetition(competitionId) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in required.");

  const existing = await arenaRepository.fetchCompetition(competitionId);
  if (!existing) throw new Error("Competition not found.");
  if (!canHardDeleteCompetition(existing, uid)) {
    throw new Error("Only the competition owner can permanently delete it.");
  }

  try {
    await arenaRepository.deleteCompetition(competitionId);
  } catch (err) {
    throw friendlyFirestoreError(err);
  }
  return true;
}

/**
 * Delete with history choice.
 * - persistHistory true (default): soft-delete for published comps; drafts hard-delete.
 * - persistHistory false: hard-delete always.
 *
 * @param {string} competitionId
 * @param {{ persistHistory?: boolean }} [options]
 * @returns {Promise<{ mode: 'soft'|'hard' }>}
 */
export async function deleteCompetitionWithHistoryChoice(
  competitionId,
  { persistHistory = true } = {}
) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in required.");

  const existing = await arenaRepository.fetchCompetition(competitionId);
  if (!existing) throw new Error("Competition not found.");

  const isDraft = normalizeCompetitionStatus(existing.status) === COMPETITION_STATUSES.DRAFT;
  const alreadySoft = isCompetitionSoftDeleted(existing.status);

  if (!persistHistory || isDraft || alreadySoft) {
    await hardDeleteCompetition(competitionId);
    return { mode: "hard" };
  }

  if (!canSoftDeleteCompetition(existing, uid)) {
    throw new Error("Only the competition owner can delete this competition.");
  }
  await softDeleteCompetition(competitionId);
  return { mode: "soft" };
}

/**
 * @param {string} competitionId
 */
export async function deleteDraftCompetition(competitionId) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in required.");

  const existing = await arenaRepository.fetchCompetition(competitionId);
  if (!existing) throw new Error("Competition not found.");
  if (!canDeleteDraftCompetition(existing, uid)) {
    throw new Error("Only draft competitions can be deleted by the owner.");
  }

  try {
    await arenaRepository.deleteCompetition(competitionId);
  } catch (err) {
    throw friendlyFirestoreError(err);
  }
  return true;
}

/**
 * @param {string} competitionId
 * @param {{ fromServer?: boolean }} [opts]
 */
export async function getCompetition(competitionId, opts = {}) {
  return arenaRepository.fetchCompetition(competitionId, opts);
}

/**
 * Organizer list — includes soft-deleted so owners can permanently purge.
 * @param {string} [uid]
 */
export async function listMyCompetitions(uid) {
  const userId = uid || currentUid();
  if (!userId) return [];
  try {
    return await arenaRepository.listCompetitionsForUser(userId);
  } catch (err) {
    throw friendlyFirestoreError(err);
  }
}

/**
 * Ensure a managed event exists on the competition (publish prerequisite).
 * @param {string} competitionId
 * @param {{ name?: string, display?: object, competition?: object }} [input]
 */
export async function addManagedEvent(competitionId, input = {}) {
  const { createEvent } = await import("./tournamentEventService");
  if (input.display && input.competition) {
    return createEvent(competitionId, {
      display: input.display,
      competition: input.competition,
    });
  }
  return createEvent(competitionId, { name: input.name });
}

/**
 * Soft-deactivate an Event (keep entries / draws). Prefer this over hard delete.
 * @param {string} competitionId
 * @param {string} eventId
 */
export async function removeManagedEvent(competitionId, eventId) {
  const { deactivateEvent } = await import("./tournamentEventService");
  await deactivateEvent(competitionId, eventId);
}

/**
 * Restore a soft-deactivated Event.
 * @param {string} competitionId
 * @param {string} eventId
 */
export async function reactivateManagedEvent(competitionId, eventId) {
  const { reactivateEvent } = await import("./tournamentEventService");
  await reactivateEvent(competitionId, eventId);
}

/**
 * Update per-category match length (sets / points).
 * @param {string} competitionId
 * @param {string} eventId
 * @param {{ setsToWin?: number, pointsToWin?: number }} matchFormat
 */
export async function updateManagedEventMatchFormat(competitionId, eventId, matchFormat) {
  const { getEvent, updateEvent } = await import("./tournamentEventService");
  const { normalizeEventMatchFormat } = await import("../domain/event/eventMatchFormat");
  const { fetchCompetition } = await import("../repositories/arenaRepository");
  const arena = await fetchCompetition(competitionId);
  const existing = await getEvent(competitionId, eventId);
  if (!existing?.competition) throw new Error("Event not found.");
  const sportId = arena?.sportId || arena?.sport;
  const nextFormat = normalizeEventMatchFormat(matchFormat, sportId);
  await updateEvent(competitionId, eventId, {
    competition: {
      ...existing.competition,
      matchFormat: nextFormat,
    },
  });
}

/**
 * Save wizard form as draft (create or update).
 * @param {object} form
 * @param {string | null} [competitionId]
 */
export async function saveCompetitionDraft(form, competitionId = null) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in required.");

  const shape = formToCompetitionShape({
    ...form,
    createdBy: uid,
    organizerUid: form.organizerUid || uid,
    status: COMPETITION_STATUSES.DRAFT,
  });

  if (competitionId) {
    const result = await updateCompetition(competitionId, {
      ...shape,
      status: COMPETITION_STATUSES.DRAFT,
    });
    return result.competition;
  }

  const created = await createCompetition(shape);
  return created;
}

export {
  validateCompetition,
  formToCompetitionShape,
  COMPETITION_STATUSES,
  COMPETITION_KINDS,
};
